import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { fetchFollowUpPatientOptions } from "../../../utils/api";
import { getErrorMessage } from "../email-center/emailFormatters";
import { patientIdOf, patientName } from "../email-center/EmailPatientPicker";

const normalizeOptions = (response) => {
  const payload = response?.data || response || {};
  return Array.isArray(payload.items) ? payload.items : [];
};

const patientSearchFieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 36,
    borderRadius: "4px",
    fontSize: "0.875rem",
  },
  "& .MuiOutlinedInput-input": {
    py: 0,
  },
};

const FollowUpPatientPicker = ({
  selectedPatientId = "",
  onSelectPatient,
  includeAll = false,
  allLabel = "All Participants",
  allDescription = "Show all follow-up records",
  height = 520,
  showHeader = true,
}) => {
  const [draft, setDraft] = useState({ name: "", patientAccountNumber: "" });
  const [applied, setApplied] = useState({ name: "", patientAccountNumber: "" });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [searchHint, setSearchHint] = useState("");

  const loadPatients = useCallback(async () => {
    if (!applied.name && !applied.patientAccountNumber) {
      setItems([]);
      setLoading(false);
      setError("");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await fetchFollowUpPatientOptions({
        name: applied.name,
        patientAccountNumber: applied.patientAccountNumber,
      });
      setItems(normalizeOptions(response));
    } catch (err) {
      console.error("Failed to load follow-up patient options:", err);
      setItems([]);
      setError(getErrorMessage(err, "Failed to load participant options."));
    } finally {
      setLoading(false);
    }
  }, [applied]);

  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  const updateDraft = (field) => (event) => {
    setDraft((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSearch = () => {
    const nextName = draft.name.trim();
    const nextMrn = draft.patientAccountNumber.trim();
    if (!nextMrn && nextName.length > 0 && nextName.length < 2) {
      setSearchHint("Type at least 2 characters to search.");
      setError("");
      setItems([]);
      setHasSearched(false);
      return;
    }
    setSearchHint("");
    setApplied({
      name: nextName,
      patientAccountNumber: nextMrn,
    });
    setHasSearched(Boolean(nextName || nextMrn));
  };

  const handleClear = () => {
    setDraft({ name: "", patientAccountNumber: "" });
    setApplied({ name: "", patientAccountNumber: "" });
    setHasSearched(false);
    setItems([]);
    setError("");
    setSearchHint("");
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  return (
    <Box sx={{ height, display: "flex", flexDirection: "column", bgcolor: "#fff" }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #d0d0d0" }}>
        {showHeader && (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Patients
            </Typography>
            {loading && <CircularProgress color="inherit" size={18} />}
          </Stack>
        )}
        <Stack
          direction="column"
          spacing={1}
          sx={{ mt: showHeader ? 1.5 : 0 }}
          onKeyDown={handleKeyDown}
        >
          <TextField
            placeholder="Name contains"
            size="small"
            value={draft.name}
            onChange={updateDraft("name")}
            sx={patientSearchFieldSx}
            fullWidth
          />
          <TextField
            placeholder="MRN starts with"
            size="small"
            value={draft.patientAccountNumber}
            onChange={updateDraft("patientAccountNumber")}
            sx={patientSearchFieldSx}
            fullWidth
          />
          <Stack direction="row" spacing={1}>
            <Tooltip title="Clear participant search" arrow>
              <Button
                variant="outlined"
                size="small"
                onClick={handleClear}
                aria-label="Clear participant search"
                sx={{ flex: 1, minWidth: 0, height: 36, px: 0 }}
              >
                <ClearIcon fontSize="small" />
              </Button>
            </Tooltip>
            <Tooltip title="Search participants" arrow>
              <Button
                variant="contained"
                size="small"
                onClick={handleSearch}
                aria-label="Search participants"
                sx={{ flex: 1, minWidth: 0, height: 36, px: 0 }}
              >
                <SearchIcon fontSize="small" />
              </Button>
            </Tooltip>
          </Stack>
        </Stack>
        {searchHint && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            {searchHint}
          </Typography>
        )}
      </Box>

      <List disablePadding sx={{ flex: 1, overflowY: "auto" }}>
        {includeAll && (
          <ListItemButton
            selected={!selectedPatientId}
            onClick={() => onSelectPatient?.(null, "all")}
            sx={{
              borderBottom: "1px solid #eeeeee",
              "&.Mui-selected": { bgcolor: "rgba(80, 0, 0, 0.10)" },
              "&.Mui-selected:hover": { bgcolor: "rgba(80, 0, 0, 0.14)" },
            }}
          >
            <ListItemText
              primary={allLabel}
              secondary={allDescription}
              primaryTypographyProps={{ fontWeight: 700, variant: "body2" }}
              secondaryTypographyProps={{ variant: "body2" }}
            />
          </ListItemButton>
        )}
        {error && (
          <Box sx={{ p: 1.5 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
        {!error && !hasSearched && (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Search by name or MRN to load participants.
            </Typography>
          </Box>
        )}
        {!error && hasSearched && items.length === 0 && !loading && (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No participants found.
            </Typography>
          </Box>
        )}
        {!error && items.map((patient) => {
          const id = patientIdOf(patient);
          const selected = selectedPatientId === id;
          const name = patientName(patient);
          return (
            <ListItemButton
              key={id}
              selected={selected}
              onClick={() => onSelectPatient?.(patient, "patient")}
              sx={{
                borderBottom: "1px solid #eeeeee",
                alignItems: "flex-start",
                py: 1.25,
                "&.Mui-selected": { bgcolor: "rgba(80, 0, 0, 0.10)" },
                "&.Mui-selected:hover": { bgcolor: "rgba(80, 0, 0, 0.14)" },
              }}
            >
              <ListItemText
                primary={name}
                secondary={patient.patientAccountNumber || "No MRN"}
                primaryTypographyProps={{
                  fontWeight: selected ? 700 : 600,
                  noWrap: true,
                  title: name,
                  variant: "body2",
                }}
                secondaryTypographyProps={{
                  noWrap: true,
                  title: patient.patientAccountNumber || "No MRN",
                  variant: "body2",
                }}
                sx={{ my: 0 }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
};

export default FollowUpPatientPicker;
